import { StateService } from '@infra/state/state.service';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import * as signalR from '@microsoft/signalr';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@infra/error-handling/alert.service';
import { AuthService } from '@infra/auth/auth.service';
import { SecurityService } from '@infra/auth/security.service';
import { NotificationService } from '@api/services/notification.service';

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private readonly alertService = inject(AlertService);
  private readonly authService = inject(AuthService);
  private readonly stateService = inject(StateService);
  private readonly notificationService = inject(NotificationService);
  private readonly securityService = inject(SecurityService);
  private readonly translateService = inject(TranslateService);

  private hubConnection: signalR.HubConnection;
  private currentAssigmentId: string;
  private currentCompanyId: string;

  async startConnection(companyId: string, assignmentId: string): Promise<void> {
    if (this.hubConnection) await this.hubConnection.stop();

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.signalR, { accessTokenFactory: () => this.authService.getToken() })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    this.hubConnection.onreconnected((): void => {
      this.joinGroups(this.currentCompanyId, this.currentAssigmentId, true);
    });

    this.hubConnection.on('NewUpdate', (minutes: number): void => {
      let message = this.translateService.instant('$notification_new_update$');
      message = message.replace('{Minutes}', minutes.toString());
      this.alertService.newRelease(message);
    });

    this.hubConnection.on('NewLead', (rawLeadCount: number, leadProviderName: string): void => {
      let message = this.translateService.instant('$notification_new_lead$');
      message = message.replace('{LeadProviderName}', leadProviderName);
      this.alertService.success(message);
      this.stateService.setRawLeadsCount(rawLeadCount);
    });

    this.hubConnection.on('EmployeeLicensesSynchronized', () => {
      this.alertService.success('$notification_employee_license_synchronized$');
    });

    this.hubConnection.on('UpdateNotifications', (): void => {
      this.notificationService.countUnread().subscribe(response => {
        this.stateService.setUnreadNotificationCount(response);
      });
    });

    this.hubConnection.on('RefreshCalendar', (): void => {
      this.stateService.refreshCalendar();
    });

    this.hubConnection.on('RefreshServices', (): void => {
      this.stateService.refreshServices();
    });

    this.hubConnection.on('RefreshReedge', () => {
      if (environment.type !== 'development') {
        this.authService.logout();
      }
    });

    this.hubConnection.on('RefreshPermissions', () => {
      this.securityService.getNewToken(this.authService.companyUrlName).subscribe(res => {
        this.authService.setLoggedIn(res.authToken);
        this.stateService.reset();
        window.location.reload();
      });
    });

    this.hubConnection.on('DeactivateUser', (companyId: string, userId: string) => {
      if (this.authService.companyId === companyId && this.authService.userId === userId) {
        this.authService.logout();
      }
    });

    this.hubConnection.on('DeactivateCompany', (companyId: string) => {
      if (this.authService.companyId === companyId) {
        this.authService.logout();
      }
    });

    this.hubConnection.on('RefreshExportData', () => {
      this.stateService.refreshExportData();
    });

    this.hubConnection.on('RefreshTask', (taskId: string) => {
      this.stateService.refreshTask(taskId);
    });

    this.hubConnection.on('RefreshPlanning', (planningId: string) => {
      this.stateService.refreshPlanning(planningId);
    });

    this.hubConnection.on('RefreshWarehouseInfo', (warehouseInfoId: string) => {
      this.stateService.refreshWarehouseInfo(warehouseInfoId);
    });

    this.hubConnection.on('RefreshRevisionProduct', (revisionProductId: string) => {
      this.stateService.refreshRevisionProduct(revisionProductId);
    });

    this.hubConnection.on('RefreshInventory', (assignmentId: string) => {
      this.stateService.refreshInventory(assignmentId);
    });

    this.hubConnection.on('RefreshAssignment', (assignmentId: string) => {
      this.stateService.refreshAssignment(assignmentId);
    });

    this.hubConnection.on('AssignmentDocumentGenerated', (assignmentId: string, assignmentFriendlyNumber: string) => {
      let message = this.translateService.instant('$notification_assignment_document_generation_completed$');
      message = message.replace('{AssignmentFriendlyNumber}', assignmentFriendlyNumber);
      this.alertService.success(message);
      this.stateService.refreshAssignmentDocuments(assignmentId);
    });

    this.hubConnection.on(
      'AssignmentDocumentGenerationFailed',
      (_assignmentId: string, assignmentFriendlyNumber: string) => {
        let message = this.translateService.instant('$error_document_generation_failure$');
        message = message.replace('{AssignmentFriendlyNumber}', assignmentFriendlyNumber);
        this.alertService.error(message);
      }
    );

    this.hubConnection.on('Move4UDocumentGenerated', (assignmentId: string, originalFileName: string) => {
      let message = this.translateService.instant('$notification_move4u_document_generated$');
      message = message.replace('{OriginalFileName}', originalFileName);
      this.alertService.success(message);
      this.stateService.refreshAssignmentDocuments(assignmentId);
    });

    this.hubConnection.on('Move4UMoveCreated', (assignmentId: string, dossierNumber: string) => {
      let message = this.translateService.instant('$notification_move4u_move_created$');
      message = message.replace('{DossierNumber}', dossierNumber);
      this.alertService.success(message);
      this.stateService.refreshAssignment(assignmentId);
    });

    this.hubConnection.on('Move4ULocationSynchronized', (_assignmentId: string, dossierNumber: string) => {
      let message = this.translateService.instant('$notification_move4u_location_synchronized$');
      message = message.replace('{DossierNumber}', dossierNumber);
      this.alertService.success(message);
    });

    this.hubConnection.on('Move4UTaskFinalized', (_assignmentId: string, dossierNumber: string) => {
      let message = this.translateService.instant('$notification_move4u_task_finalized$');
      message = message.replace('{DossierNumber}', dossierNumber);
      this.alertService.success(message);
      this.stateService.refreshInventory(assignmentId);
    });

    this.hubConnection.on(
      'Move4UPieceMoved',
      (
        _assignmentId: string,
        pieceFriendlyNumber: number | null,
        oldWarehousePosition: string,
        warehousePosition: string
      ) => {
        let message = this.translateService.instant('$notification_move4u_piece_moved$');
        message = message.replace('{PieceFriendlyNumber}', pieceFriendlyNumber);
        message = message.replace('{OldWarehousePosition}', oldWarehousePosition);
        message = message.replace('{WarehousePosition}', warehousePosition);
        this.alertService.success(message);
      }
    );

    try {
      await this.hubConnection.start();

      if (companyId) {
        await this.joinGroups(companyId, assignmentId, false);
      }
    } catch (error) {
      console.log('Failed to establish SignalR connection: ' + error);
    }
  }

  private async joinGroups(companyId: string, assignmentId: string, reconnect: boolean): Promise<void> {
    if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      if ((companyId && this.currentCompanyId != companyId) || reconnect) {
        if (this.currentCompanyId) {
          await this.hubConnection.invoke('LeaveGroup', this.currentCompanyId);
        }
        await this.hubConnection.invoke('JoinGroup', companyId);
        this.currentCompanyId = companyId;
      }

      if ((assignmentId && this.currentAssigmentId != assignmentId) || reconnect) {
        if (this.currentAssigmentId) {
          await this.hubConnection.invoke('LeaveGroup', this.currentAssigmentId);
        }
        await this.hubConnection.invoke('JoinGroup', assignmentId);
        this.currentAssigmentId = assignmentId;
      }
    }
  }
}
